import Phaser from 'phaser';
import GameConfig from './GameConfig';
import { playJumpSound, playKnifeAttackSound, playHurtSound } from './audio';
import { playDeathMusic, showGameOver } from './death';

const RAT_FRAMES = {
  walkStart:        0,
  walkEnd:          3,
  attackStart:      6,
  attackEnd:        9,
  attackCrouchStart: 13,
  attackCrouchEnd:  15,
  climbStart:       19,
  climbEnd:         22,
  tailWiggleStart:  24,
  tailWiggleEnd:    29,
  blinkFrame:       5,
  dieStart:         30,
  dieEnd:           32,
};

export function createPlayer(scene) {
  scene.player = scene.physics.add.sprite(90, 460, 'rat', 0);
  scene.player.setBounce(0);
  scene.player.setCollideWorldBounds(true);
  scene.player.health = 3;
  scene.player.isDead = false;
  scene.player.body.setSize(10, 29);
  scene.player.body.setOffset(28, 14);
  scene.player.lastTimeGrounded = 0;
  scene.player.isPerched = false;

  scene.player.knockbackTimer = 0;
  scene.player.idleTimer = 0;
  scene.playerInvincible = false;
  scene.canJump = true;
  scene.isAttacking = false;
  scene.playerRect = new Phaser.Geom.Rectangle();
  scene.hasSlingshot = false;
  scene.slingshotAmmo = 0;

  const pg = scene.make.graphics({ add: false });
  pg.fillStyle(0x000000);
  pg.fillRect(0, 0, 3, 3);
  pg.generateTexture('pebble', 3, 3);
  pg.destroy();

  scene.anims.create({
    key: 'ratDie',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.dieStart, end: RAT_FRAMES.dieEnd }),
    frameRate: 6,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratWalk',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.walkStart, end: RAT_FRAMES.walkEnd }),
    frameRate: 10,
    repeat: -1
  });
  scene.anims.create({
    key: 'ratAttack',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.attackStart, end: RAT_FRAMES.attackEnd }),
    frameRate: 15,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratAttackCrouch',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.attackCrouchStart, end: RAT_FRAMES.attackCrouchEnd }),
    frameRate: 15,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratClimb',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.climbStart, end: RAT_FRAMES.climbEnd }),
    frameRate: 8,
    repeat: -1
  });
  scene.anims.create({
    key: 'ratBlink',
    frames: [
      { key: 'rat', frame: 0 },
      { key: 'rat', frame: RAT_FRAMES.blinkFrame },
      { key: 'rat', frame: 0 },
    ],
    frameRate: 6,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratTailWiggle',
    frames: scene.anims.generateFrameNumbers('rat', { start: RAT_FRAMES.tailWiggleStart, end: RAT_FRAMES.tailWiggleEnd }),
    frameRate: 8,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratWalkAttack',
    frames: scene.anims.generateFrameNumbers('rat', { start: 54, end: 57 }),
    frameRate: 15,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratJumpAttack',
    frames: scene.anims.generateFrameNumbers('rat', { start: 60, end: 63 }),
    frameRate: 15,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratSlingshot',
    frames: scene.anims.generateFrameNumbers('rat', { frames: [36, 37, 38, 42, 43] }),
    frameRate: 15,
    repeat: 0
  });
  scene.anims.create({
    key: 'ratSlingshotCrouch',
    frames: scene.anims.generateFrameNumbers('rat', { frames: [44, 45, 48, 49, 50] }),
    frameRate: 15,
    repeat: 0
  });

  scene.physics.add.collider(scene.player, scene.environmentLayer, (_player, tile) => {
    if (tile.properties?.spikes) damagePlayer(scene, 0.5);
  });

  scene.cameras.main.startFollow(scene.player);
}

export function damagePlayer(scene, amount) {
  if (scene.playerInvincible || scene.player.isDead) return;
  scene.player.health = Math.max(0, scene.player.health - amount);
  playHurtSound();

  if (scene.player.health <= 0) {
    scene.player.isDead = true;
    scene.statusPanel?.update();
    scene.player.setVelocity(0, 0);
    scene.player.body.setAllowGravity(false);
    scene.player.setDepth(20);
    (scene.cacti || []).filter(c => c.active).forEach(c => c.setVelocityX(0));
    if (scene.backgroundMusic) { scene.backgroundMusic.stopParts(); scene.backgroundMusic.pause(); }
    playDeathMusic(scene);

    const worldObjects = [
      scene.environmentLayer,
      ...(scene.cacti || []).filter(c => c.active),
      ...(scene.spiders || []).filter(s => s.active),
      ...(scene.roaches || []).filter(r => r.active),
      ...(scene.bombs   || []).filter(b => b.active),
    ...(scene.croc?.active ? [scene.croc] : []),
      ...scene.coins.getChildren(),
      ...scene.hearts.getChildren(),
      ...scene.slingshots.getChildren(),
      ...scene.oneUps.getChildren(),
      ...scene.doors.getChildren(),
    ];
    scene.tweens.add({
      targets: worldObjects,
      alpha: 0,
      duration: 150,
      onComplete: () => scene.player.anims.play('ratDie'),
    });
    scene.player.once('animationcomplete-ratDie', () => {
      const cam = scene.cameras.main;
      const overlay = scene.add.rectangle(0, 0, cam.width, cam.height, 0xffffff)
        .setOrigin(0, 0).setScrollFactor(0).setDepth(10).setAlpha(0);
      scene.tweens.add({
        targets: overlay,
        alpha: 1,
        duration: 700,
        onComplete: () => {
          scene.time.delayedCall(1000, () => {
            scene.tweens.add({
              targets: scene.player,
              alpha: 0,
              duration: 1200,
              onComplete: () => {
                const lives = Math.max(0, scene.statusPanel.lives - 1);
                if (lives <= 0) {
                  showGameOver(scene);
                } else {
                  if (scene.backgroundMusic) { scene.backgroundMusic.dispose(); scene.backgroundMusic = null; }
                  if (scene.deathMusic) scene.deathMusic.stop();
                  if (scene.physicsPanel) scene.physicsPanel.destroy();
                  scene.scene.restart({ lives, musicEnabled: scene.isMusicPlaying });
                }
              }
            });
          });
        }
      });
    });
    return;
  }

  scene.playerInvincible = true;
  scene.tweens.add({
    targets: scene.player,
    alpha: 0.2,
    duration: 80,
    yoyo: true,
    repeat: 6,
    onComplete: () => { scene.player.setAlpha(1); scene.playerInvincible = false; }
  });
}

export function updatePlayer(scene, time, delta) {
  const keys   = scene.inputKeys;
  const player = scene.player;

  if (player.knockbackTimer > 0) {
    player.knockbackTimer -= delta;
    return;
  }

  const isGrounded = player.body.touching.down || player.body.blocked.down;
  if (isGrounded) player.isPerched = false;

  const tile = scene.environmentLayer.getTileAtWorldXY(player.x, player.y);
  const isOnLadder = tile?.properties?.topLadder ||
                     tile?.properties?.midLadder  ||
                     tile?.properties?.bottomLadder;

  if (isOnLadder && (keys.up.isDown || keys.down.isDown)) {
    player.isClimbing = true;
    player.isPerched = false;
    player.body.setAllowGravity(false);
  } else if (player.isClimbing && !isOnLadder && !isGrounded && player.body.velocity.y <= 0) {
    // Climbed off the top of a freestanding ladder — perch
    player.isPerched = true;
    player.isClimbing = false;
    player.setVelocityY(0);
    player.setVelocityX(0);
    player.body.setAllowGravity(false);
  } else if (!isOnLadder) {
    player.isClimbing = false;
    if (!player.isPerched) player.body.setAllowGravity(true);
  }

  if (player.isPerched) {
    player.setVelocityX(0);
    player.setVelocityY(0);
    player.anims.stop();
    if (keys.jump.isDown) {
      player.isPerched = false;
      player.body.setAllowGravity(true);
      player.setVelocityY(-GameConfig.JUMP_THRUST);
      playJumpSound();
    } else if (keys.left.isDown) {
      player.isPerched = false;
      player.body.setAllowGravity(true);
      player.setVelocityX(-GameConfig.SPEED);
      player.flipX = true;
    } else if (keys.right.isDown) {
      player.isPerched = false;
      player.body.setAllowGravity(true);
      player.setVelocityX(GameConfig.SPEED);
      player.flipX = false;
    } else if (keys.down.isDown) {
      player.isPerched = false;
      player.isClimbing = true;
      player.body.setAllowGravity(false);
      player.setVelocityY(GameConfig.CLIMB_SPEED);
    }
    return;
  }

  if (player.isClimbing) {
    // Ease toward ladder center when not pressing left/right
    if (!keys.left.isDown && !keys.right.isDown && tile) {
      const tileCenter = tile.pixelX + tile.width / 2;
      const diff = tileCenter - player.x;
      if (Math.abs(diff) > 0.5) { player.x += diff * 0.2; }
      else                       { player.x = tileCenter; }
    }
    player.setVelocityX(0);
    if (keys.up.isDown) {
      player.setVelocityY(-GameConfig.CLIMB_SPEED);
      player.anims.play('ratClimb', true);
    } else if (keys.down.isDown) {
      player.setVelocityY(GameConfig.CLIMB_SPEED);
      player.anims.play('ratClimb', true);
    } else {
      player.setVelocityY(0);
      player.anims.stop();
    }
    if (keys.left.isDown)       { player.setVelocityX(-GameConfig.SPEED); player.flipX = true; }
    else if (keys.right.isDown) { player.setVelocityX(GameConfig.SPEED);  player.flipX = false; }
    if (Phaser.Input.Keyboard.JustDown(keys.jump)) {
      player.isClimbing = false;
      player.body.setAllowGravity(true);
      player.setVelocityY(-GameConfig.JUMP_THRUST);
      playJumpSound();
    }
    return;
  }

  player.setVelocityX(0);

  // Crouch attack
  if (keys.down.isDown && Phaser.Input.Keyboard.JustDown(keys.attack) && !scene.isAttacking) {
    scene.isAttacking = true;
    if (scene.hasSlingshot) {
      player.anims.play('ratSlingshotCrouch', true);
      scene.time.delayedCall(200, () => { if (!player.isDead) fireSlingshot(scene, true); });
    } else {
      player.anims.play('ratAttackCrouch', true);
      playKnifeAttackSound();
      scene.time.delayedCall(100, () => tryHitEnemies(scene, true));
    }
    scene.time.delayedCall(350, () => {
      scene.isAttacking = false;
      player.setTexture('rat', keys.down.isDown ? 4 : 0);
    });
    return;
  }

  // Attack — animation varies by state; no return so movement continues
  if (Phaser.Input.Keyboard.JustDown(keys.attack) && !scene.isAttacking) {
    scene.isAttacking = true;
    if (scene.hasSlingshot) {
      player.anims.play('ratSlingshot', true);
      scene.time.delayedCall(200, () => { if (!player.isDead) fireSlingshot(scene); });
      player.once('animationcomplete-ratSlingshot', () => {
        scene.isAttacking = false;
        if (!keys.left.isDown && !keys.right.isDown) player.setTexture('rat', 0);
      });
    } else {
      const isMoving = keys.left.isDown || keys.right.isDown;
      const animKey = !isGrounded ? 'ratJumpAttack' : isMoving ? 'ratWalkAttack' : 'ratAttack';
      player.anims.play(animKey, true);
      playKnifeAttackSound();
      scene.time.delayedCall(100, () => tryHitEnemies(scene, !isGrounded));
      scene.time.delayedCall(250, () => {
        scene.isAttacking = false;
        if (!keys.left.isDown && !keys.right.isDown) player.setTexture('rat', 0);
      });
    }
  }

  // Crouch
  if (keys.down.isDown && isGrounded && !scene.isAttacking) {
    player.setTexture('rat', 12);
    player.anims.stop();
  } else {
    if (keys.left.isDown) {
      player.setVelocityX(-GameConfig.SPEED);
      player.idleTimer = 0;
      if (isGrounded && !scene.isAttacking) player.anims.play('ratWalk', true);
      player.flipX = true;
    } else if (keys.right.isDown) {
      player.setVelocityX(GameConfig.SPEED);
      player.idleTimer = 0;
      if (isGrounded && !scene.isAttacking) player.anims.play('ratWalk', true);
      player.flipX = false;
    } else if (isGrounded && !scene.isAttacking) {
      const currentAnim = player.anims.currentAnim?.key;
      const playingIdle = (currentAnim === 'ratBlink' || currentAnim === 'ratTailWiggle') && player.anims.isPlaying;
      if (!playingIdle) {
        player.anims.stop();
        player.setTexture('rat', 0);
        player.idleTimer += delta;
        if (player.idleTimer > 3000) {
          player.idleTimer = 0;
          const pick = Math.random() < 0.5 ? 'ratBlink' : 'ratTailWiggle';
          player.anims.play(pick);
          player.once(`animationcomplete-${pick}`, () => player.setTexture('rat', 0));
        }
      }
    }
  }

  // Jump with coyote time
  if (isGrounded) player.lastTimeGrounded = time;
  const coyoteTimeAvailable = time - player.lastTimeGrounded <= GameConfig.COYOTE_TIME;
  if (keys.jump.isDown && (isGrounded || coyoteTimeAvailable) && scene.canJump) {
    player.idleTimer = 0;
    player.setVelocityY(-GameConfig.JUMP_THRUST);
    player.setTexture('rat', 1);
    scene.canJump = false;
    playJumpSound();
    player.lastTimeGrounded = 0;
  }
  if (keys.jump.isUp) scene.canJump = true;

  // Jump/land texture
  if (!isGrounded && !scene.isAttacking && player.frame.name !== GameConfig.JUMP_FRAME_INDEX) {
    player.setTexture('rat', 4);
  }
  if (isGrounded && !scene.isAttacking && player.frame.name === GameConfig.JUMP_FRAME_INDEX) {
    player.setTexture('rat', 0);
  }
}

function tryHitEnemies(scene, isLowAttack = false) {
  const enemies = [
    ...(scene.cacti || []),
    ...(scene.spiders || []),
    ...(scene.roaches || []),
    ...(scene.bombs   || []),
    ...(scene.croc?.active ? [scene.croc] : []),
  ].filter(e => e?.active && !e.isDying && e.isVulnerable !== false && (!e.requiresCrouch || isLowAttack));

  const facing = scene.player.flipX ? -1 : 1;
  for (const enemy of enemies) {
    const dx = enemy.body.center.x - scene.player.body.center.x;
    const dy = enemy.body.center.y - scene.player.body.center.y;
    if (Math.abs(dx) < 40 && Math.abs(dy) < 20) {
      if (Math.sign(dx) === facing || Math.abs(dx) < 8) {
        enemy.hit(facing);
        break;
      }
    }
  }
}

function fireSlingshot(scene, isCrouch = false) {
  const player = scene.player;
  const facing = player.flipX ? -1 : 1;

  const pebble = scene.physics.add.image(
    player.x + facing * 18,
    isCrouch ? player.y + 16 : player.y + 5,
    'pebble'
  );
  pebble.body.setAllowGravity(false);
  pebble.setVelocityX(facing * 600);
  scene.time.delayedCall(1500, () => pebble?.destroy());

  playKnifeAttackSound();

  const enemies = [
    ...(scene.cacti  || []),
    ...(scene.spiders || []),
    ...(scene.roaches || []),
    ...(scene.bombs   || []),
    ...(scene.croc?.active ? [scene.croc] : []),
  ].filter(e => e?.active && !e.isDying);

  for (const enemy of enemies) {
    scene.physics.add.overlap(pebble, enemy, () => {
      if (!pebble.active) return;
      enemy.hit?.(facing);
      pebble.destroy();
    });
  }

  scene.slingshotAmmo--;
  if (scene.slingshotAmmo <= 0) scene.hasSlingshot = false;
}
